import Drawer from '@material-ui/core/Drawer';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useContext, useMemo, useState} from 'react';

import {useHandleScroll} from '../../../../pages/HomePage/HomePageSidebar/useHandleScroll';
import {Analytics} from '../../../../services/analytics';
import {globalRegistryType} from '../../common/utils';
import {ResizableDrawer} from '../../workflows/ResizableDrawer';
import {RegistryMembersContext} from '../RegistryMembersContextProvider';
import {useAddRegistryMembers} from './hooks';
import {MemberInviteOptions} from './MemberInviteOptions';

type AddRegistryMemberDrawerProps = {
  open: boolean;
  closeDrawer: () => void;
  registry: globalRegistryType;
};

export const AddRegistryMemberDrawer = ({
  open,
  closeDrawer,
  registry,
}: AddRegistryMemberDrawerProps) => {
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };
  const scrolled = useHandleScroll();
  const drawerPaddingTop = `${Math.max(80 - scrolled.scrollHeight, 20)}px`;

  const handleCloseDrawer = () => {
    setSelectedUserIds([]);
    setSelectedTeamIds([]);
    closeDrawer();
  };

  const {registryMembers, registryTeamMembers} = useContext(
    RegistryMembersContext
  );
  const currentRegistryMembers = registryMembers.map(member => member.id);
  const currentRegistryTeamMembers = registryTeamMembers.map(
    member => member.id
  );

  const {
    id: registryProjectId,
    entityName,
    registryProjectName,
    registryName,
  } = registry;

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const usersToAdd: string[] = useMemo(
    () => selectedUserIds.filter(u => !currentRegistryMembers?.includes(u)),
    [selectedUserIds, currentRegistryMembers]
  );
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const teamsToAdd: string[] = useMemo(
    () => selectedTeamIds.filter(t => !currentRegistryTeamMembers?.includes(t)),
    [selectedTeamIds, currentRegistryTeamMembers]
  );

  const {addRegistryMembers, isLoading} = useAddRegistryMembers(
    entityName,
    registryProjectName
  );

  const isButtonDisabled = useMemo(() => {
    const noMembersToAdd = usersToAdd.length === 0 && teamsToAdd.length === 0;
    return noMembersToAdd || isLoading;
  }, [usersToAdd, teamsToAdd, isLoading]);

  const onClickConfirm = async () => {
    await addRegistryMembers(registryProjectId, usersToAdd, teamsToAdd);
    if (usersToAdd.length > 0) {
      Analytics.track('Registry interaction', {
        action: 'add members',
        entityName,
        registryProjectName,
        registryName,
      });
    }
    if (teamsToAdd.length > 0) {
      Analytics.track('Registry interaction', {
        action: 'add team members',
        entityName,
        registryProjectName,
        registryName,
      });
    }
    handleCloseDrawer();
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleCloseDrawer}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <Tailwind style={{width: '100%', height: '100%'}}>
          <div
            className={`h-full w-full px-24 pb-20`}
            style={{paddingTop: drawerPaddingTop}}>
            <div className="flex h-full w-full flex-col">
              <div className="mb-20 flex items-center">
                <div className="text-xl font-semibold leading-8">
                  Add access
                </div>
                <div className="flex-1 text-right">
                  <Button
                    onClick={handleCloseDrawer}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </div>
              </div>
              <div className="-mx-24 h-1 bg-moon-250"></div>
              <div className="w-full flex-1 overflow-auto p-4">
                <div className="my-24 flex w-full flex-col ">
                  <div className="text-base font-semibold leading-8">
                    Include users and teams
                  </div>
                  <div className="flex items-center">
                    <MemberInviteOptions
                      selectedUsers={selectedUserIds}
                      setSelectedUsers={setSelectedUserIds}
                      selectedTeams={selectedTeamIds}
                      setSelectedTeams={setSelectedTeamIds}
                    />
                  </div>
                </div>
              </div>
              <div className="-mx-20 h-1 bg-moon-250"></div>
              <div className="flex pt-20">
                <Button
                  className="w-full"
                  variant="primary"
                  size="large"
                  disabled={isButtonDisabled}
                  onClick={onClickConfirm}>
                  Add access
                </Button>
              </div>
            </div>
          </div>
        </Tailwind>
      </ResizableDrawer>
    </Drawer>
  );
};
